import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ProductCardWrapper } from "../Cards/ProductCardWrapper";
import { ProductCardAsyncWrapper } from "../Cards/ProductCardAsyncWrapper";
import { CategoryQuestionsWrapper } from "../Cards/CategoryQuestionsWrapper";
import { CategoryCard } from "../Cards/CategoryCard";
import ProductReviewWrapper from "../Cards/ProductReviewWrapper";
import ProductCardCompetitorWrapper from "../Cards/ProductCardCompetitorWrapper";
import CompetitorProductCard from "../Cards/CompetitorProductCard";
import { getCitationFilePath } from "../../api";
import { PriceSource, SiteId } from "../../api/models";
import { getText } from "./TextUtils";
import Typewriter from "./TypeWriter";
import styles from "./Answer.module.css";

// Module-level Set to track questions that have been logged already
const loggedQuestions = new Set<string>();

export type ReactParsedAnswer = {
    answerContent: React.ReactNode[];
    citations: string[];
};

const ScrollableMarkdownTable = ({ children }: { children?: React.ReactNode }) => (
    <div className={styles.tableScrollContainer} role="region" aria-label="Scrollable table" tabIndex={0}>
        <table>{children}</table>
    </div>
);

/**
 * Cleans SKU values by removing markdown formatting and other special characters
 * that might be added by the LLM (e.g., **1892112** -> 1892112)
 */
function cleanSku(sku: string): string {
    if (!sku) return "";

    // Remove markdown bold formatting (**text**)
    let cleaned = sku.replace(/\*\*(.*?)\*\*/g, "$1");

    // Remove other markdown formatting
    cleaned = cleaned.replace(/\*(.*?)\*/g, "$1"); // *text*
    cleaned = cleaned.replace(/`(.*?)`/g, "$1"); // `text`

    // Remove any remaining asterisks
    cleaned = cleaned.replace(/\*/g, "");

    // Trim whitespace
    cleaned = cleaned.trim();

    return cleaned;
}

// Extract balanced JSON objects from a string (handles nested braces and strings)
function extractBalancedJsonObjects(input: string): string[] {
    const results: string[] = [];
    let depth = 0;
    let inString = false;
    let escape = false;
    let startIndex: number | null = null;
    for (let i = 0; i < input.length; i++) {
        const ch = input[i];
        if (escape) {
            escape = false;
            continue;
        }
        if (ch === "\\") {
            if (inString) escape = true;
            continue;
        }
        if (ch === '"') {
            inString = !inString;
            continue;
        }
        if (inString) continue;
        if (ch === "{") {
            if (depth === 0) startIndex = i;
            depth++;
        } else if (ch === "}") {
            depth--;
            if (depth === 0 && startIndex !== null) {
                results.push(input.slice(startIndex, i + 1));
                startIndex = null;
            }
        }
    }
    return results;
}

// Best-effort sanitizer: convert JS-like object to strict JSON by quoting bare keys and normalizing quotes
function sanitizeToJson(input: string): string {
    let s = input.trim();
    // Replace single quotes with double quotes
    s = s.replace(/'/g, '"');
    // Quote unquoted keys after { or ,
    s = s.replace(/([\{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":');
    return s;
}

export function parseAnswerToReact(
    answer: string,
    isStreaming: boolean,
    onCitationClicked: (citationFilePath: string) => void,
    onCategorySubmit?: (formattedAnswer: string) => void,
    spinnerActive: boolean = false,
    onAnswersUpdate?: (formattedAnswer: string) => void,
    clearTriggerId?: number,
    priceSource: PriceSource = "web",
    siteId: SiteId = "PUBLIC_GR"
): ReactParsedAnswer {
    const citations: string[] = [];
    const fragments: React.ReactNode[] = [];

    // Shared inline markdown renderer — same component type in both paths prevents remount flash
    const inlineMd = (text: string, key: string, className?: string) => (
        <span key={key} className={className}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    table: ScrollableMarkdownTable,
                    p: ({ children }) => <span style={{ margin: 0, padding: 0, whiteSpace: "pre-wrap" }}>{children}</span>,
                    ul: ({ children }) => <span style={{ margin: 0, padding: 0 }}>{children}</span>,
                    ol: ({ children }) => <span style={{ margin: 0, padding: 0 }}>{children}</span>,
                    li: ({ children }) => <span style={{ margin: 0, padding: 0 }}>• {children} </span>,
                    br: () => <span> </span>
                }}
            >
                {text}
            </ReactMarkdown>
        </span>
    );

    if (isStreaming) {
        // ✅ Detect if this is a product template early (check for products/Products field)
        const isProductTemplate = /"(?:products|Products)"\s*:\s*\[/.test(answer) || /"competitor_products"\s*:\s*\[/.test(answer);

        // ✅ Streaming mode: animate intro/response_text/text/conclusion (support both lowercase and capitalized)
        const simpleKeys = ["intro", "response_text", "text", "conclusion", "Intro", "Response_text", "Text", "Conclusion"];
        simpleKeys.forEach(key => {
            const regex = new RegExp(`"${key}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)"`);
            const match = regex.exec(answer);
            if (match) {
                const text = match[1];
                const keyLower = key.toLowerCase();

                // If it's a product template and this is intro/conclusion, add className for spacing
                const className =
                    isProductTemplate && (keyLower === "intro" || keyLower === "conclusion")
                        ? keyLower === "intro"
                            ? "product-intro"
                            : "product-conclusion"
                        : undefined;

                fragments.push(inlineMd(text, keyLower, className));
            }
        });

        // ✅ Process streaming category questions if present (revert to main behavior: only when a proper questions array appears)
        const questionsArrayRegex = /"questions"\s*:\s*\[(.*)/s;
        const questionsArrayMatch = questionsArrayRegex.exec(answer);
        if (questionsArrayMatch) {
            // Keep as-is: rely on properly formatted JSON streaming
            const questionsContent = questionsArrayMatch[1];
            const objects = extractBalancedJsonObjects(questionsContent);
            objects.forEach((objStr, idx) => {
                try {
                    const questionObj = JSON.parse(objStr);
                    if (questionObj && questionObj.choices) {
                        // Use component's internal stable key for persistence; only React key here
                        const base = (questionObj.question || "").trim().toLowerCase();
                        const ch = (questionObj.choices || []).map((c: any) => (typeof c === "string" ? c : c.label || "").trim().toLowerCase());
                        const reactKey = `q|${base}|c|${ch.join(";")}`;
                        fragments.push(<CategoryCard key={reactKey} questionObj={questionObj} onAnswerChange={() => {}} />);
                    }
                } catch {
                    // ignore
                }
            });
        }

        // No global scanning in main behavior

        // ✅ Process streaming competitor_products
        const competitorProductsArrayMatch = answer.match(/"competitor_products"\s*:\s*(\[[\s\S]*?\])/);
        if (competitorProductsArrayMatch && competitorProductsArrayMatch[1]) {
            try {
                const competitorProducts = JSON.parse(competitorProductsArrayMatch[1]);
                competitorProducts.forEach((compProd: any, index: number) => {
                    fragments.push(
                        <ProductCardCompetitorWrapper
                            key={`competitor-${index}`}
                            competitorProduct={compProd}
                            onCitationClicked={onCitationClicked}
                            spinnerActive={spinnerActive}
                            priceSource={priceSource}
                            siteId={siteId}
                        />
                    );
                });
            } catch (e) {
                console.error("Error parsing streaming competitor products:", e);
            }
        }

        // ✅ Process streaming products (support both lowercase and capitalized)
        const productsArrayRegex = /"(?:products|Products)"\s*:\s*\[(.*)/s;
        const productsArrayMatch = productsArrayRegex.exec(answer);
        if (productsArrayMatch) {
            const productsContent = productsArrayMatch[1];
            const productObjRegex = /{[^}]*}/g;
            let pMatch: RegExpExecArray | null;
            while ((pMatch = productObjRegex.exec(productsContent)) !== null) {
                const prodStr = pMatch[0];
                try {
                    const product = JSON.parse(prodStr);
                    if (!product.competitor_details && !product.competitor_name) {
                        const rawSkuValue = product.sku || product.sku_id || product.skuid;
                        const skuValue = cleanSku(rawSkuValue);
                        if (skuValue && !product.content && !product.pricing) {
                            fragments.push(
                                <ProductCardAsyncWrapper
                                    key={`product-${skuValue || pMatch.index}`}
                                    product={{ ...product, sku: skuValue, sku_id: skuValue, skuid: skuValue }}
                                    onCitationClicked={onCitationClicked}
                                    spinnerActive={spinnerActive}
                                    priceSource={priceSource}
                                    siteId={siteId}
                                />
                            );
                        } else {
                            fragments.push(
                                <ProductCardWrapper
                                    key={`product-${skuValue || pMatch.index}`}
                                    product={{ ...product, sku: skuValue, sku_id: skuValue, skuid: skuValue }}
                                    onCitationClicked={onCitationClicked}
                                    priceSource={priceSource}
                                    siteId={siteId}
                                />
                            );
                        }
                        if (product.sourcepage && !citations.includes(product.sourcepage)) {
                            citations.push(product.sourcepage);
                        }
                    }
                } catch (e) {
                    console.error("Error parsing product (streaming):", e);
                }
            }
        }

        // ✅ Fallback: If no JSON structure found during streaming, render markdown immediately
        // Skip if content looks like structured JSON — it will be parsed properly once more data arrives
        if (fragments.length === 0 && answer.trim() && !answer.trim().startsWith("{")) {
            const cleanedAnswer = answer.replace(/\[\[.*?\]\]/g, "").trim();

            // Don't use Typewriter - the streaming itself provides the typewriter effect!
            // Apply ReactMarkdown to render proper formatting (lists, bold, spacing) during streaming
            fragments.push(
                <ReactMarkdown
                    key="plain-streaming"
                    remarkPlugins={[remarkGfm]}
                    components={{
                        table: ScrollableMarkdownTable,
                        // Keep paragraphs inline to avoid layout shifts during streaming
                        p: ({ children }) => <span style={{ whiteSpace: 'pre-wrap' }}>{children}</span>,
                        // Render lists with proper styling
                        ul: ({ children }) => <ul style={{ marginTop: "8px", marginBottom: "8px", paddingLeft: "20px" }}>{children}</ul>,
                        ol: ({ children }) => <ol style={{ marginTop: "8px", marginBottom: "8px", paddingLeft: "20px" }}>{children}</ol>,
                        li: ({ children }) => <li style={{ marginBottom: "4px" }}>{children}</li>,
                        // Render text formatting
                        strong: ({ children }) => <strong>{children}</strong>,
                        em: ({ children }) => <em>{children}</em>,
                        // Render code blocks and inline code
                        code: ({ children, ...props }) => (
                            <code style={{ backgroundColor: "var(--bg-code)", color: "var(--text-primary)", padding: "2px 4px", borderRadius: "3px" }} {...props}>
                                {children}
                            </code>
                        ),
                        pre: ({ children }) => (
                            <pre
                                style={{
                                    backgroundColor: "var(--bg-code)",
                                    color: "var(--text-primary)",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    overflow: "auto",
                                    marginTop: "8px",
                                    marginBottom: "8px"
                                }}
                            >
                                {children}
                            </pre>
                        ),
                        // Render line breaks
                        br: () => <br />
                    }}
                >
                    {cleanedAnswer}
                </ReactMarkdown>
            );
        }

        return { answerContent: fragments, citations };
    } else {
        // ✅ Non-streaming mode: parse complete JSON — same inlineMd as streaming = no remount flash
        try {
            let jsonCandidate = answer.trim();
            const objects = extractBalancedJsonObjects(jsonCandidate);
            const looksLikeJson = jsonCandidate.startsWith("{") || jsonCandidate.startsWith("[");
            const isSingleBalanced = objects.length === 1 && objects[0].length === jsonCandidate.length;

            // Only attempt parse if the entire trimmed string is a single balanced JSON object/array
            if (!(looksLikeJson && isSingleBalanced)) {
                // Try to recover the last fully balanced object if present
                if (objects.length > 0) {
                    jsonCandidate = objects[objects.length - 1];
                } else {
                    // Not a complete JSON payload - will be caught and handled as plain text
                    throw new Error("Not a complete JSON payload");
                }
            }

            const data = JSON.parse(jsonCandidate);

            // ✅ Sentences
            if (data.sentences && Array.isArray(data.sentences)) {
                data.sentences.forEach((sentence: string, index: number) => {
                    fragments.push(
                        <p key={`sentence-${index}`}>
                            {inlineMd(sentence, `sentence-inner-${index}`)}
                        </p>
                    );
                });
            }
            // ✅ Product reviews
            else if (data.review_products && Array.isArray(data.review_products)) {
                fragments.push(<ProductReviewWrapper key="review-wrapper" reviewData={data} />);
            }
            // ✅ Competitor products
            else if (data.competitor_products && Array.isArray(data.competitor_products)) {
                if (data.intro) {
                    fragments.push(inlineMd(getText(data.intro), "intro"));
                }
                data.competitor_products.forEach((compProd: any, index: number) => {
                    fragments.push(
                        <ProductCardCompetitorWrapper
                            key={`competitor-${index}`}
                            competitorProduct={compProd}
                            onCitationClicked={onCitationClicked}
                            spinnerActive={spinnerActive}
                            priceSource={priceSource}
                            siteId={siteId}
                        />
                    );
                });
                if (data.conclusion) {
                    fragments.push(inlineMd(getText(data.conclusion), "conclusion"));
                }
            }
            // ✅ Regular products (support both lowercase and capitalized field names)
            else if ((data.products || data.Products) && Array.isArray(data.products || data.Products)) {
                const products = data.products || data.Products;
                const intro = data.intro || data.Intro;
                const conclusion = data.conclusion || data.Conclusion;

                if (intro) {
                    fragments.push(inlineMd(getText(intro), "intro", "product-intro"));
                }
                products.forEach((product: any, index: number) => {
                    const rawSkuValue = product.sku || product.sku_id || product.skuid;
                    const skuValue = cleanSku(rawSkuValue);
                    if (skuValue && !product.content && !product.pricing) {
                        fragments.push(
                            <ProductCardAsyncWrapper
                                key={`product-${skuValue || index}`}
                                product={{ ...product, sku: skuValue, sku_id: skuValue, skuid: skuValue }}
                                onCitationClicked={onCitationClicked}
                                spinnerActive={spinnerActive}
                                priceSource={priceSource}
                                siteId={siteId}
                            />
                        );
                    } else {
                        fragments.push(
                            <ProductCardWrapper
                                key={`product-${skuValue || index}`}
                                product={{ ...product, sku: skuValue, sku_id: skuValue, skuid: skuValue }}
                                onCitationClicked={onCitationClicked}
                                priceSource={priceSource}
                                siteId={siteId}
                            />
                        );
                    }
                    if (product.sourcepage && !citations.includes(product.sourcepage)) {
                        citations.push(product.sourcepage);
                    }
                });
                if (conclusion) {
                    fragments.push(inlineMd(getText(conclusion), "conclusion", "product-conclusion"));
                }
            }
            // ✅ Category questions
            else if (data.questions && Array.isArray(data.questions)) {
                fragments.push(
                    <CategoryQuestionsWrapper
                        key="category-questions-wrapper"
                        intro={getText(data.intro)}
                        questions={data.questions}
                        conclusion={getText(data.conclusion)}
                        onAnswersUpdate={onAnswersUpdate}
                        clearTriggerId={clearTriggerId}
                    />
                );
            }
            // ✅ Fallback plain text
            else {
                if (data.intro) {
                    fragments.push(inlineMd(getText(data.intro), "intro"));
                }
                if (data.text) {
                    fragments.push(inlineMd(getText(data.text), "text"));
                }
                if (data.response_text) {
                    fragments.push(inlineMd(getText(data.response_text), "response_text"));
                }
                if (data.questions && Array.isArray(data.questions)) {
                    fragments.push(
                        <ul key="questions">
                            {data.questions.map((item: { question: string }, index: number) => {
                                return (
                                    <li key={`question-${index}`}>
                                        {inlineMd(item.question, `question-inner-${index}`)}
                                    </li>
                                );
                            })}
                        </ul>
                    );
                }
                if (data.products && Array.isArray(data.products)) {
                    data.products.forEach((product: any, index: number) => {
                        const skuValue = product.sku || product.sku_id || product.skuid;
                        if (skuValue && !product.content && !product.pricing) {
                            fragments.push(
                                <ProductCardAsyncWrapper
                                    key={`product-${skuValue || index}`}
                                    product={product}
                                    onCitationClicked={onCitationClicked}
                                    spinnerActive={spinnerActive}
                                    priceSource={priceSource}
                                    siteId={siteId}
                                />
                            );
                        } else {
                            fragments.push(
                                <ProductCardWrapper
                                    key={`product-${skuValue || index}`}
                                    product={product}
                                    onCitationClicked={onCitationClicked}
                                    priceSource={priceSource}
                                    siteId={siteId}
                                />
                            );
                        }
                        if (product.sourcepage && !citations.includes(product.sourcepage)) {
                            citations.push(product.sourcepage);
                        }
                    });
                }
                if (data.conclusion) {
                    fragments.push(inlineMd(getText(data.conclusion), "conclusion"));
                }
            }
        } catch (error) {
            // Graceful fallback for non-JSON responses (e.g., from n8n plain text)
            // Only log error if it looks like it was supposed to be JSON
            const looksLikeJson = answer.trim().startsWith("{") || answer.trim().startsWith("[");
            if (looksLikeJson) {
                // Only log if it's not the expected "Not a complete JSON payload" error
                const errorMessage = error instanceof Error ? error.message : String(error);
                if (!errorMessage.includes("Not a complete JSON payload")) {
                    console.error("Error parsing complete JSON:", error);
                }
                // For malformed JSON, render with inline markdown
                fragments.push(inlineMd(getText(answer), "fallback"));
            } else {
                const cleanedAnswer = getText(answer)
                    .replace(/\[\[.*?\]\]/g, "")
                    .trim();

                // console.log("ℹ️ Non-JSON response detected, rendering with markdown formatting (no animation)");
                // For plain text (like n8n), render with ReactMarkdown to maintain formatting
                // that was shown during streaming (avoid re-animating with Typewriter)
                fragments.push(
                    <ReactMarkdown
                        key="plain-text"
                        remarkPlugins={[remarkGfm]}
                        components={{
                            table: ScrollableMarkdownTable,
                            // Keep paragraphs inline to avoid layout shifts
                            p: ({ children }) => <span style={{ whiteSpace: 'pre-wrap' }}>{children}</span>,
                            // Render lists with proper styling
                            ul: ({ children }) => <ul style={{ marginTop: "8px", marginBottom: "8px", paddingLeft: "20px" }}>{children}</ul>,
                            ol: ({ children }) => <ol style={{ marginTop: "8px", marginBottom: "8px", paddingLeft: "20px" }}>{children}</ol>,
                            li: ({ children }) => <li style={{ marginBottom: "4px" }}>{children}</li>,
                            // Render text formatting
                            strong: ({ children }) => <strong>{children}</strong>,
                            em: ({ children }) => <em>{children}</em>,
                            // Render code blocks and inline code
                            code: ({ children, ...props }) => (
                                <code style={{ backgroundColor: "var(--bg-code)", color: "var(--text-primary)", padding: "2px 4px", borderRadius: "3px" }} {...props}>
                                    {children}
                                </code>
                            ),
                            pre: ({ children }) => (
                                <pre
                                    style={{
                                        backgroundColor: "var(--bg-code)",
                                        color: "var(--text-primary)",
                                        padding: "8px",
                                        borderRadius: "4px",
                                        overflow: "auto",
                                        marginTop: "8px",
                                        marginBottom: "8px"
                                    }}
                                >
                                    {children}
                                </pre>
                            ),
                            thead: ({ children }) => (
                                <thead style={{ backgroundColor: "var(--bg-secondary)", fontWeight: "600" }}>{children}</thead>
                            ),
                            tbody: ({ children }) => <tbody>{children}</tbody>,
                            tr: ({ children }) => <tr style={{ borderBottom: "1px solid var(--border-secondary)" }}>{children}</tr>,
                            th: ({ children }) => (
                                <th
                                    style={{
                                        padding: "10px 12px",
                                        textAlign: "left",
                                        borderBottom: "2px solid var(--border-primary)",
                                        color: "var(--text-primary)"
                                    }}
                                >
                                    {children}
                                </th>
                            ),
                            td: ({ children }) => (
                                <td style={{ padding: "10px 12px", color: "var(--text-primary)", borderBottom: "1px solid var(--border-secondary)" }}>
                                    {children}
                                </td>
                            ),
                            // Render line breaks
                            br: () => <br />
                        }}
                    >
                        {cleanedAnswer}
                    </ReactMarkdown>
                );
            }
        }
        return { answerContent: fragments, citations };
    }
}
